import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import type { HeadcountReconciliationResult } from '@shared/types'
import { useApi } from '../contexts/ApiContext'
import { useModalFiles, isRoster, isStructured } from './useModalFiles'
import ModalFileList from './ModalFileList'
import HeadcountPanel from './HeadcountPanel'
import RosterSelectionModal from './RosterSelectionModal'
import { useRosterValidation, RosterErrorList } from './useRosterValidation'
import { parseStructuredFilesForHeadcount } from '../utils/parseStructuredFilesForHeadcount'
import { parseCsvToExcelResult } from '../utils/parseCsvToExcelResult'

interface Props {
  files: Array<{ base64: string; name: string }>
  isRunning?: boolean
  onConfirm: (updated: Array<{ base64: string; name: string }>) => void
  onReset: () => void
  onClose: () => void
}

export default function EditDocumentsModal({ files, isRunning, onConfirm, onReset, onClose }: Props) {
  const api = useApi()
  const { localFiles, setLocalFiles, addError, addFilesFromList, openDialog } = useModalFiles(files)
  const [localResult, setLocalResult] = useState<HeadcountReconciliationResult | null>(null)
  const [parsingHeadcount, setParsingHeadcount] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const [selectingRoster, setSelectingRoster] = useState(false)
  const { errors: rosterErrors, validating, validateThen, clearErrors } = useRosterValidation()
  useEffect(() => { clearErrors() }, [localFiles, clearErrors])
  const rosterNames = localFiles.filter((f) => isRoster(f.name)).map((f) => f.name)

  const parseStructuredFiles = useCallback(
    (filesToParse: Array<{ name: string; base64: string }>) =>
      parseStructuredFilesForHeadcount(filesToParse, (b64) => api.parseExcel(b64), parseCsvToExcelResult),
    [api]
  )

  useEffect(() => {
    // Only XLSX/CSV can be previewed; with more than one roster (JSON included) there is no single headcount yet.
    const structuredFiles = localFiles.filter((f) => isStructured(f.name))
    if (structuredFiles.length === 0 || localFiles.filter((f) => isRoster(f.name)).length > 1) { setLocalResult(null); setParsingHeadcount(false); return }
    let cancelled = false
    setParsingHeadcount(true)
    parseStructuredFiles(structuredFiles)
      .then((result) => { if (!cancelled) setLocalResult(result) })
      .catch(() => { if (!cancelled) setLocalResult(null) })
      .finally(() => { if (!cancelled) setParsingHeadcount(false) })
    return () => { cancelled = true }
  }, [localFiles, parseStructuredFiles])

  const fileKey = (f: { name: string; base64: string }) => `${f.name}:${f.base64.length}`
  const hasChanges = localFiles.map(fileKey).sort().join('|') !== files.map(fileKey).sort().join('|')
  const canSave = localFiles.length > 0 && !isRunning && !parsingHeadcount && !validating && hasChanges

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full mx-4 flex flex-col" style={{ maxWidth: 540, maxHeight: '90vh' }}>

        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Edit Documents</h2>
          <p className="text-xs text-gray-500 mt-0.5">Add or remove files without resetting your session.</p>
          {isRunning && (
            <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              A generation is currently running. Wait for it to finish before saving changes.
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <ModalFileList
            files={localFiles}
            heading="Current Files"
            emptyMessage="No files — add at least one below before saving."
            addError={addError}
            onRemove={(name) => setLocalFiles((prev) => prev.filter((f) => f.name !== name))}
            onDrop={(fileList) => void addFilesFromList(fileList)}
            onOpenDialog={() => void openDialog()}
          />
          <HeadcountPanel
            parsingHeadcount={parsingHeadcount}
            result={localResult}
            context="save"
            multipleRosters={rosterNames.length > 1}
            hasUnpreviewableRoster={localFiles.some((f) => f.name.toLowerCase().endsWith('.json'))}
          />
          <RosterErrorList errors={rosterErrors} />
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-100 space-y-2">
          <div className="flex flex-row gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              disabled={!canSave}
              onClick={() => { if (rosterNames.length > 1) setSelectingRoster(true); else void validateThen(localFiles, onConfirm) }}
              className="flex-1 py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              Save Changes
            </button>
          </div>
          {!hasChanges && localFiles.length > 0 && (
            <p className="text-xs text-gray-400 text-center">No changes made</p>
          )}
          <div className="flex justify-center">
            {confirmReset ? (
              <span className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">Reset everything and start over?</span>
                <button
                  onClick={() => { setConfirmReset(false); onReset() }}
                  className="px-2.5 py-1 bg-red-500 text-white rounded font-semibold hover:bg-red-600 transition-colors"
                >
                  Yes, reset
                </button>
                <button
                  onClick={() => setConfirmReset(false)}
                  className="text-gray-400 hover:text-gray-600 underline"
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                onClick={() => setConfirmReset(true)}
                className="text-xs text-red-500 hover:text-red-700 underline"
              >
                Reset all & start over
              </button>
            )}
          </div>
        </div>
      </div>
      {selectingRoster && (
        <RosterSelectionModal
          rosterNames={rosterNames}
          onConfirm={(selected) => {
            setSelectingRoster(false)
            void validateThen(localFiles.filter((f) => !isRoster(f.name) || f.name === selected), onConfirm)
          }}
          onCancel={() => setSelectingRoster(false)}
        />
      )}
    </div>,
    document.body
  )
}
